import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Image, Tag, X, Send, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/store';
import { Avatar } from './ui';

export default function Composer({ groupHandle, shopId, onPosted }) {
  const { user, shops } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({
    body: '', title: '', image_url: '', type: 'post',
    price: '', old_price: '', valid_until: '', link_url: '',
    shop_id: shopId || '',
  });

  const myShops = (shops || []).filter(s => s.status === 'approved');
  const canOffer = myShops.length > 0;

  const post = useMutation({
    mutationFn: () => api.post('/posts', {
      ...f, group_handle: groupHandle,
      shop_id: f.shop_id || undefined,
      price: f.price || undefined, old_price: f.old_price || undefined,
      valid_until: f.valid_until || undefined,
    }),
    onSuccess: ({ data }) => {
      toast.success(f.type === 'offer' ? 'Offer published to 100% of the group! 🔥' : 'Posted!');
      setF({ body: '', title: '', image_url: '', type: 'post', price: '', old_price: '', valid_until: '', link_url: '', shop_id: shopId || '' });
      setOpen(false);
      qc.invalidateQueries(['feed']);
      onPosted?.(data);
    },
    onError: e => toast.error(e.message),
  });

  async function upload(file) {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/upload/posts', fd);
      setF(v => ({ ...v, image_url: data.url }));
    } catch (e) { toast.error(e.message); } finally { setUploading(false); }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="card p-3 w-full flex items-center gap-3 text-left hover:shadow-lg transition">
        <Avatar src={user?.avatar_url} name={user?.username} size={38} />
        <span className="text-sm text-ink-400 font-semibold flex-1">
          {groupHandle ? `Post in ${groupHandle}…` : 'Share something…'}
        </span>
        <div className="chip-brand">+5💎 per share</div>
      </button>
    );
  }

  return (
    <div className="card p-4 animate-fade-up">
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-1.5">
          {[['post', '💬 Post'], ...(canOffer ? [['offer', '🔥 Offer'], ['announcement', '📢 News']] : [])].map(([k, l]) => (
            <button key={k} onClick={() => setF({ ...f, type: k, shop_id: k !== 'post' ? (f.shop_id || myShops[0]?.id) : f.shop_id })}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition
                ${f.type === k ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-500'}`}>{l}</button>
          ))}
        </div>
        <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-lg bg-ink-100 text-ink-500"><X size={14} className="mx-auto" /></button>
      </div>

      {f.type !== 'post' && myShops.length > 1 && (
        <select className="input mb-2 text-sm" value={f.shop_id} onChange={e => setF({ ...f, shop_id: e.target.value })}>
          {myShops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}

      {f.type !== 'post' && (
        <input className="input mb-2 font-bold" placeholder="Offer title — e.g. Fresh tomatoes 20% off"
          value={f.title} onChange={e => setF({ ...f, title: e.target.value })} />
      )}

      <textarea className="input min-h-[90px] resize-none" placeholder="What's happening?"
        value={f.body} onChange={e => setF({ ...f, body: e.target.value })} />

      {f.type === 'offer' && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          <div><label className="label">New price</label>
            <input className="input" type="number" step="0.01" value={f.price}
              onChange={e => setF({ ...f, price: e.target.value })} /></div>
          <div><label className="label">Old price</label>
            <input className="input" type="number" step="0.01" value={f.old_price}
              onChange={e => setF({ ...f, old_price: e.target.value })} /></div>
          <div><label className="label">Valid until</label>
            <input className="input" type="date" value={f.valid_until}
              onChange={e => setF({ ...f, valid_until: e.target.value })} /></div>
        </div>
      )}

      {f.image_url && (
        <div className="relative mt-3">
          <img src={f.image_url} className="w-full max-h-56 object-cover rounded-xl" alt="" />
          <button onClick={() => setF({ ...f, image_url: '' })}
            className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-ink-900/70 text-white"><X size={14} className="mx-auto" /></button>
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => upload(e.target.files[0])} />
        <button onClick={() => fileRef.current.click()} disabled={uploading}
          className="btn-ghost btn-sm">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Image size={14} />} Photo
        </button>
        {f.type === 'offer' && (
          <input className="input !py-1.5 text-xs flex-1" placeholder="Link (optional)"
            value={f.link_url} onChange={e => setF({ ...f, link_url: e.target.value })} />
        )}
        <div className="flex-1" />
        <button onClick={() => post.mutate()} disabled={post.isPending || (!f.body && !f.title)}
          className="btn-primary btn-sm">
          {post.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Publish
        </button>
      </div>
    </div>
  );
}